import React, { useState, useEffect, useRef } from "react";
import { Card, Table, Input, InputNumber, Button, Typography, Space, message, Select, Tooltip, Checkbox } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ArrowRightOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { ColumnConfigModal } from "../../common/ColumnConfigModal";
import BufferedTextInput from "../../common/BufferedTextInput";
import { isDuplicatePlotNo } from "../../../utils/plotNoValidation";
import { useTender } from "../../../contexts/TenderContext";
import MainLayout from "../../layout/MainLayout";
import { useExcelImportExport } from "../../../hooks/useExcelImportExport";
import { ExcelImportExport } from "../../common/ExcelImportExport";
import { ExcelColumn } from "../../../utils/excelUtils";
import { COLORS } from "../../../utils/constants";
import { useNavigate } from "react-router-dom";
import { prepareTenderForAPI } from "../../../utils/storage";
import { useCreateBillOfQuantities } from "../../../hooks/useBillOfQuantities";
import NumberInput from "../../common/NumberInput";
import { useAuth } from "../../../hooks/useAuth";
import { useScrollToTop } from "../../../hooks/useScrollToTop";
// import { useChangeDetection } from "../../../hooks/useChangeDetection";
import { useTableRenderDeferral } from "../../../hooks/useTableRenderDeferral";
import TableLoadingPlaceholder from "../../common/TableLoadingPlaceholder";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";
import { useGroundworksTotalCalculations } from "../../../hooks/useGroundworksTotalCalculations";
import { forceSaveBothBoqEndpoints } from "../../../utils/boqVersionUtils";
import { useItemLibrary } from "../../../hooks/useItemLibrary";
import { addColumnCodes } from '@/utils/addColumnCodes';
import { SCAFFOLD_REFERENCES } from '@/utils/scaffoldReferenceMapping';
import { normaliseDecimalMmInLabel } from "../../../utils/dimensionLabel";

const { Title, Text } = Typography;
const { Option } = Select;

const excelColumns: ExcelColumn[] = [
  { key: "plotNo", title: "Plot No" },
  { key: "housetype", title: "House type" },
  { key: "scaffoldPlatform", title: "Scaffold Platform" },
  { key: "loadingBay4x2m", title: "Loading bay 4x2m" },
  { key: "stairAccess3x3m", title: "Stair Access 3x3m" },
  // Scaffold Platform
  { key: "scaffoldLevelCompact", title: "Level & Compact (m²)" },
  { key: "scaffoldGeotextileLayer", title: "Geotextile Layer (m²)" },
  { key: "scaffoldCrush6F2", title: "xxxmm crush 6F2 (m²)" },
  { key: "scaffoldType1", title: "xxmm Type1 (m²)" },
  // Loading Bays
  { key: "loadingLevelCompact", title: "Level & Compact (m²)" },
  { key: "loadingGeotextileLayer", title: "Geotextile Layer (m²)" },
  { key: "loadingCrush6F2", title: "xxxmm crush 6F2 (m²)" },
  { key: "loadingType1", title: "xxmm Type1 (m²)" },
  // Stair Access
  { key: "stairLevelCompact", title: "Level & Compact (m²)" },
  { key: "stairGeotextileLayer", title: "Geotextile Layer (m²)" },
  { key: "stairCrush6F2", title: "xxxmm crush 6F2 (m²)" },
  { key: "stairType1", title: "xxmm Type1 (m²)" },
];

interface ScaffoldRow {
  key: string;
  plotNo: string;
  housetype: string;
  plotType: "plot" | "garage";
  // Base measurements
  scaffoldPlatform: number;
  loadingBay4x2m: number;
  stairAccess3x3m: number;
  // Scaffold Platform
  scaffoldLevelCompact: number;
  scaffoldGeotextileLayer: number;
  scaffoldCrush6F2: number;
  scaffoldType1: number;
  // Loading Bays
  loadingLevelCompact: number;
  loadingGeotextileLayer: number;
  loadingCrush6F2: number;
  loadingType1: number;
  // Stair Access
  stairLevelCompact: number;
  stairGeotextileLayer: number;
  stairCrush6F2: number;
  stairType1: number;
}

const Scaffold: React.FC = () => {
  // Scroll to top on page load
  useScrollToTop();

  const { state: tender, updateField, saveTender } = useTender();
  const { user } = useAuth();
  const navigate = useNavigate();
  const createBoqMutation = useCreateBillOfQuantities();

  // Format column title to display field name and unit separately
  const formatColumnTitle = React.useCallback(
    (baseTitle: string): React.ReactNode => {
      const unitMatch = baseTitle.match(/\s+\((m[²³]|m|Nr|t)\)$/);
      let titleWithoutUnit = baseTitle;
      let unit = "";

      if (unitMatch) {
        unit = unitMatch[1];
        titleWithoutUnit = baseTitle.replace(/\s+\((m[²³]|m|Nr|t)\)$/, "");
      }

      if (unit) {
        return (
          <div className="text-center">
            <Tooltip title={titleWithoutUnit}>
              <div className="gw-header-title">{titleWithoutUnit}</div>
            </Tooltip>
            <div className="gw-header-unit text-xs text-gray-500">{unit}</div>
          </div>
        );
      }
      return (
        <Tooltip title={baseTitle}>
          <div className="gw-header-title">{baseTitle}</div>
        </Tooltip>
      );
    },
    []
  );

  // Checkbox state logic for groundworks totals
  const getCheckboxState = (key: string, fallbackPercent: number): boolean => {
    const saved = localStorage.getItem(key);
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return fallbackPercent > 0;
  };

  const preliminariesPercent = tender.globalSettings?.groundworks?.preliminaries || 0;
  const ohpPercent = tender.globalSettings?.groundworks?.overheadAndProfit || 0;
  const discountPercent = tender.globalSettings?.groundworks?.discount || 0;

  const includePreliminaries = getCheckboxState("groundworks_includePreliminariesDisplay", preliminariesPercent);
  const includeOhp = getCheckboxState("groundworks_includeOhpDisplay", ohpPercent);
  const includeDiscount = getCheckboxState("groundworks_includeDiscountDisplay", discountPercent);

  const groundworksTotals = useGroundworksTotalCalculations({
    includePreliminaries,
    includeOhp,
    includeDiscount,
  });
  const { data: itemLibraryData } = useItemLibrary(tender.region || "");

  const [data, setData] = useState<ScaffoldRow[]>([]);
  const [plotTypeFilter, setPlotTypeFilter] = useState<"plots" | "garages">("plots");
  const [columnHeaders, setColumnHeaders] = useState<Record<string, string>>(
    {}
  );
  // Also carries the "Geotextile Layer" checkbox per tab (as "true"/"false"
  // under scaffoldGeotextileEnabled / loadingGeotextileEnabled / stairGeotextileEnabled).
  const [dimensionValues, setDimensionValues] = useState<
    Record<string, string>
  >({});
  // Temporary state for pending header changes (only applied when user clicks Apply)
  const [tempDimensionValues, setTempDimensionValues] = useState<
    Record<string, string>
  >({});
  const [tempColumnHeaders, setTempColumnHeaders] = useState<
    Record<string, string>
  >({});
  const [activeTab, setActiveTab] = useState<string>("scaffold-platform");
  // Defers the Scaffold Platform / Loading Bays / Stair Access tables' mount
  // by one paint cycle so a tender with a lot of plots doesn't make this
  // step look frozen while a table's render/layout blocks the main thread -
  // see useTableRenderDeferral's own doc comment. Re-keyed on `activeTab`
  // since switching tabs here unmounts/mounts a brand new Table, not just a
  // dataSource update - so the loader should reappear on every tab switch,
  // not just the initial mount. Purely a perceived-lag fix: once it flips
  // false the tables render exactly as before.
  const isTableLoading = useTableRenderDeferral([activeTab]);
  const [tableKey, setTableKey] = useState(0);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [keysToDelete, setKeysToDelete] = useState<string[]>([]);
  const [selectedScaffoldKeys, setSelectedScaffoldKeys] = useState<React.Key[]>([]);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  // Ref to track latest groundworks state for save operations
  const groundworksRef = useRef(tender?.groundworks);
  useEffect(() => {
    groundworksRef.current = tender?.groundworks;
  }, [tender?.groundworks]);

  // Track previous configModalVisible state to detect when modal opens
  const prevConfigModalVisibleRef = useRef(configModalVisible);

  // Reset temp values when modal opens (configModalVisible goes from false to true)
  useEffect(() => {
    if (configModalVisible && !prevConfigModalVisibleRef.current) {
      // Modal just opened, reset temp values to current values
      const newDimValues = { ...dimensionValues };
      // Parse loadingBaySize into W and H components
      if (newDimValues["loadingBaySize"]) {
        const match = newDimValues["loadingBaySize"].match(/^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)m?$/);
        if (match) {
          newDimValues["loadingBaySizeW"] = match[1];
          newDimValues["loadingBaySizeH"] = match[2];
        }
      }
      // Parse stairAccessSize into W and H components
      if (newDimValues["stairAccessSize"]) {
        const match = newDimValues["stairAccessSize"].match(/^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)m?$/);
        if (match) {
          newDimValues["stairAccessSizeW"] = match[1];
          newDimValues["stairAccessSizeH"] = match[2];
        }
      }
      setTempDimensionValues(newDimValues);
      setTempColumnHeaders({ ...columnHeaders });
    }
    prevConfigModalVisibleRef.current = configModalVisible;
  }, [configModalVisible, dimensionValues, columnHeaders]);

  // Change detection hook
  /* const {
    hasChanges,
    trackFieldChange,
    trackRowAdded,
    trackRowDeleted,
    resetChanges,
    getChangeSummary,
  } = useChangeDetection<ScaffoldRow[]>({
    pageId: "scaffold",
    onChangeDetected: (changes) => {
    },
  }); */

  // Force table re-render when switching tabs
  React.useEffect(() => {
    setTableKey((prev) => prev + 1);
  }, [activeTab]);

  const {
    importLoading,
    importModalVisible,
    setImportModalVisible,
    handleExportExcel,
    handleDownloadTemplate,
    handleImportExcel: baseHandleImportExcel,
  } = useExcelImportExport<ScaffoldRow>({
    filename: "Scaffold",
    sheetName: "Scaffold",
    columns: excelColumns,
    data: data,
    onImport: (importedData) => {
      const processedData = importedData.map((row, index) => {
        const emptyRow = createEmptyRow(`imported_${Date.now()}_${index}`);
        const merged = { ...emptyRow, ...row };

        // Round all numeric fields to 2 decimals
        Object.keys(merged).forEach((key) => {
          if (typeof merged[key] === "number" && key !== "key") {
            merged[key] = Math.round(merged[key] * 1000) / 1000;
          }
        });

        return merged;
      });
      setData(processedData);
      updateField("groundworks", {
        ...tender.groundworks,
        scaffoldTable: processedData,
      });
    },
    skipValidation: true,
  });

  const handleImportExcel = async (file: File) => {
    setImportModalVisible(false);

    try {
      const XLSX = await import("xlsx");
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];

          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            raw: true,
            blankrows: false,
          }) as any[][];


          let headerRowIndex = -1;
          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (
              row.some(
                (cell) =>
                  String(cell).includes("Plot") && String(cell).includes("No")
              )
            ) {
              headerRowIndex = i;
              break;
            }
          }


          if (headerRowIndex === -1) {
            alert("Could not find header row with 'Plot No'");
            return;
          }

          const headers = jsonData[headerRowIndex];

          const allDataRows = jsonData.slice(headerRowIndex + 1);

          // Skip only the first row (totals row)
          const dataRows = allDataRows.slice(1);


          // Find column indices by matching header names
          const plotNoIdx = headers.findIndex(
            (h: any) => String(h).includes("Plot") && String(h).includes("No")
          );
          const housetypeIdx = headers.findIndex(
            (h: any) =>
              String(h).toLowerCase().includes("house") &&
              String(h).toLowerCase().includes("type")
          );

          // Base measurements
          const scaffoldPlatformIdx = headers.findIndex(
            (h: any) =>
              String(h).toLowerCase().includes("scaffold") &&
              String(h).toLowerCase().includes("platform")
          );
          const loadingBay4x2mIdx = headers.findIndex(
            (h: any) =>
              String(h).toLowerCase().includes("loading") &&
              String(h).toLowerCase().includes("bay")
          );
          const stairAccess3x3mIdx = headers.findIndex(
            (h: any) =>
              String(h).toLowerCase().includes("stair") &&
              String(h).toLowerCase().includes("access")
          );

          // Build a column mapping by finding columns sequentially in the header
          const columnMapping: { [key: string]: number } = {};

          let currentSearchIndex = 0; // Track position to search from

          excelColumns.forEach((col, colArrayIndex) => {
            // For basic columns, use findIndex
            if (colArrayIndex < 5) {
              // Plot No, House type, Scaffold Platform, Loading bay, Stair Access
              if (col.key === "plotNo") {
                columnMapping[col.key] = plotNoIdx;
              } else if (col.key === "housetype") {
                columnMapping[col.key] = housetypeIdx;
              } else if (col.key === "scaffoldPlatform") {
                columnMapping[col.key] = scaffoldPlatformIdx;
              } else if (col.key === "loadingBay4x2m") {
                columnMapping[col.key] = loadingBay4x2mIdx;
              } else if (col.key === "stairAccess3x3m") {
                columnMapping[col.key] = stairAccess3x3mIdx;
              }
              currentSearchIndex = columnMapping[col.key] + 1;
            } else {
              // For other columns, search sequentially from currentSearchIndex
              // This ensures we find columns in order and don't match the wrong ones
              const foundIndex = headers.findIndex((h: any, idx: number) => {
                if (idx < currentSearchIndex) return false; // Only search forward

                const headerText = String(h || "")
                  .toLowerCase()
                  .trim();
                const titleText = col.title.toLowerCase();

                // Match based on the column title, but be flexible with dimensions
                // For dynamic columns (with xxmm, xxxmm), match the non-dimension part
                if (/(x{2,}mm?)/i.test(col.title)) {
                  // This is a dynamic column - match the text pattern after the dimension
                  // e.g., "xxxmm crush 6F2" should match "150mm crush 6F2"
                  // Remove dimension pattern AND parentheses
                  const patternWithoutDimension = titleText
                    .replace(/(x{2,}mm?)/gi, "")
                    .replace(/\(.*?\)/g, "")
                    .trim();
                  const headerWithoutParens = headerText
                    .replace(/\(.*?\)/g, "")
                    .trim();


                  // Check if the header contains all words from the pattern
                  const patternWords = patternWithoutDimension
                    .split(/\s+/)
                    .filter((w) => w.length > 0);
                  const match = patternWords.every((word) =>
                    headerWithoutParens.includes(word)
                  );

                  if (match) {
                  }

                  return match;
                } else {
                  // Non-dynamic column - do exact matching
                  const titleWords = titleText
                    .replace(/\(.*?\)/g, "")
                    .trim()
                    .split(/\s+/);
                  return titleWords.every((word) => headerText.includes(word));
                }
              });

              if (foundIndex >= 0) {
                columnMapping[col.key] = foundIndex;
                currentSearchIndex = foundIndex + 1;
              } else {
              }
            }
          });


          // Extract column headers and dimension values using the correct column mapping
          const extractedHeaders: Record<string, string> = {};
          const extractedDimensionValues: Record<string, string> = {};

          // Build multi-row headers to search (up to 4 rows below headerRowIndex)
          const headerRowsToSearch: string[][] = [];
          for (let r = headerRowIndex; r <= Math.min(headerRowIndex + 4, jsonData.length - 1); r++) {
            if (jsonData[r]) {
              headerRowsToSearch.push(jsonData[r].map((cell: any) => String(cell || "").trim()));
            }
          }

          // Define fixed text for each dynamic column to extract only the leading dimension
          const dimensionFixedText: Record<string, string> = {
            scaffoldCrush6F2: "crush 6F2",
            scaffoldType1: "Type1",
            loadingCrush6F2: "crush 6F2",
            loadingType1: "Type1",
            stairCrush6F2: "crush 6F2",
            stairType1: "Type1",
          };

          excelColumns.forEach((col) => {
            const actualColumnIndex = columnMapping[col.key];
            if (actualColumnIndex !== undefined && actualColumnIndex >= 0) {
              const headerValue = String(
                headers[actualColumnIndex] || ""
              ).trim();
              extractedHeaders[col.key] = headerValue;

              // Check if this column has dimension patterns in default title (xxmm, xxxmm, etc.)
              if (/(x{2,}mm?)/i.test(col.title)) {
                // Search across multiple header rows for the dimension value
                const fixedText = dimensionFixedText[col.key];
                let found = false;

                for (const headerRow of headerRowsToSearch) {
                  if (found) break;
                  const cellValue = headerRow[actualColumnIndex];
                  if (!cellValue) continue;

                  if (fixedText) {
                    // Find the fixed text portion and extract the leading dimension before it
                    const fixedTextIndex = cellValue.toLowerCase().indexOf(fixedText.toLowerCase());
                    if (fixedTextIndex > 0) {
                      const leadingText = cellValue.substring(0, fixedTextIndex).trim();
                      // Extract numeric dimension (supports decimals like 12.34)
                      const dimMatch = leadingText.match(/(\d+(?:\.\d+)?)\s*mm/i) || leadingText.match(/(\d+(?:\.\d+)?)\s*$/);
                      if (dimMatch) {
                        extractedDimensionValues[col.key] = dimMatch[1];
                        found = true;
                      }
                    }
                  }

                  if (!found) {
                    // Fallback: extract first number followed by mm (not matching digits in words like 6F2, Type1)
                    const dimensionMatch = cellValue.match(/\b(\d+(?:\.\d+)?)\s*mm\b/i);
                    if (dimensionMatch) {
                      extractedDimensionValues[col.key] = dimensionMatch[1];
                      found = true;
                    }
                  }
                }
              }
            }
          });

          // Extract Loading bay size from header (e.g., "Loading bay 4.5x2.5m" → W=4.5, H=2.5)
          const loadingBayHeader = extractedHeaders["loadingBay4x2m"] || "";
          const loadingBaySizeMatch = loadingBayHeader.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*m/i);
          if (loadingBaySizeMatch) {
            extractedDimensionValues["loadingBaySizeW"] = loadingBaySizeMatch[1];
            extractedDimensionValues["loadingBaySizeH"] = loadingBaySizeMatch[2];
            extractedDimensionValues["loadingBaySize"] = `${loadingBaySizeMatch[1]}x${loadingBaySizeMatch[2]}m`;
          } else {
            // Search multi-row headers for loading bay size
            for (const headerRow of headerRowsToSearch) {
              const cellValue = headerRow[columnMapping["loadingBay4x2m"]];
              if (!cellValue) continue;
              const match = cellValue.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*m/i);
              if (match) {
                extractedDimensionValues["loadingBaySizeW"] = match[1];
                extractedDimensionValues["loadingBaySizeH"] = match[2];
                extractedDimensionValues["loadingBaySize"] = `${match[1]}x${match[2]}m`;
                break;
              }
            }
          }

          // Extract Stair Access size from header (e.g., "Stair Access 3.5x3.5m" → W=3.5, H=3.5)
          const stairAccessHeader = extractedHeaders["stairAccess3x3m"] || "";
          const stairAccessSizeMatch = stairAccessHeader.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*m/i);
          if (stairAccessSizeMatch) {
            extractedDimensionValues["stairAccessSizeW"] = stairAccessSizeMatch[1];
            extractedDimensionValues["stairAccessSizeH"] = stairAccessSizeMatch[2];
            extractedDimensionValues["stairAccessSize"] = `${stairAccessSizeMatch[1]}x${stairAccessSizeMatch[2]}m`;
          } else {
            // Search multi-row headers for stair access size
            for (const headerRow of headerRowsToSearch) {
              const cellValue = headerRow[columnMapping["stairAccess3x3m"]];
              if (!cellValue) continue;
              const match = cellValue.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*m/i);
              if (match) {
                extractedDimensionValues["stairAccessSizeW"] = match[1];
                extractedDimensionValues["stairAccessSizeH"] = match[2];
                extractedDimensionValues["stairAccessSize"] = `${match[1]}x${match[2]}m`;
                break;
              }
            }
          }


          setColumnHeaders(extractedHeaders);
          setDimensionValues(extractedDimensionValues);

          // Scaffold Platform section
          const scaffoldLevelCompactIdx =
            columnMapping["scaffoldLevelCompact"] ?? -1;
          const scaffoldGeotextileLayerIdx =
            columnMapping["scaffoldGeotextileLayer"] ?? -1;
          const scaffoldCrush6F2Idx = columnMapping["scaffoldCrush6F2"] ?? -1;
          const scaffoldType1Idx = columnMapping["scaffoldType1"] ?? -1;

          // Loading Bays section
          const loadingLevelCompactIdx =
            columnMapping["loadingLevelCompact"] ?? -1;
          const loadingGeotextileLayerIdx =
            columnMapping["loadingGeotextileLayer"] ?? -1;
          const loadingCrush6F2Idx = columnMapping["loadingCrush6F2"] ?? -1;
          const loadingType1Idx = columnMapping["loadingType1"] ?? -1;


          // Stair Access section
          const stairLevelCompactIdx = columnMapping["stairLevelCompact"] ?? -1;
          const stairGeotextileLayerIdx =
            columnMapping["stairGeotextileLayer"] ?? -1;
          const stairCrush6F2Idx = columnMapping["stairCrush6F2"] ?? -1;
          const stairType1Idx = columnMapping["stairType1"] ?? -1;



          // Detect "GARAGES" separator row to tag rows as plot or garage
          let isGarageSection = false;
          const allImportedRows: ScaffoldRow[] = [];
          const filteredRows = dataRows.filter(
            (row) =>
              row &&
              row.length > 0 &&
              row.some(
                (cell) => cell !== null && cell !== undefined && cell !== ""
              )
          );

          filteredRows.forEach((row, index) => {
            const firstCell = String(row[plotNoIdx] || "").trim().toUpperCase();
            if (firstCell === "GARAGES" || firstCell === "GARAGE") {
              isGarageSection = true;
              return; // Skip the separator row itself
            }
            const plotNoStr = String(row[plotNoIdx] || "").trim();
            const hasPlotNo =
              plotNoStr !== "" &&
              plotNoStr !== "0" &&
              plotNoStr !== "null" &&
              plotNoStr !== "undefined";
            const housetypeStr = String(row[housetypeIdx] || "").trim();
            const hasValidHouseType =
              housetypeStr !== "" &&
              !housetypeStr.toLowerCase().includes("total") &&
              !housetypeStr.toLowerCase().includes("sum") &&
              isNaN(Number(housetypeStr)) && // Not purely numeric
              housetypeStr.match(/[a-zA-Z]/); // Contains letters
            if (!hasPlotNo || !hasValidHouseType) return;

            allImportedRows.push({
              key: `imported_${Date.now()}_${index}`,
              plotNo: String(row[plotNoIdx] || ""),
              housetype: String(row[housetypeIdx] || ""),
              plotType: isGarageSection ? "garage" : "plot",
              scaffoldPlatform: Number(row[scaffoldPlatformIdx] || 0),
              loadingBay4x2m: Number(row[loadingBay4x2mIdx] || 0),
              stairAccess3x3m: Number(row[stairAccess3x3mIdx] || 0),
              scaffoldLevelCompact: Number(row[scaffoldLevelCompactIdx] || 0),
              scaffoldGeotextileLayer: Number(
                row[scaffoldGeotextileLayerIdx] || 0
              ),
              scaffoldCrush6F2: Number(row[scaffoldCrush6F2Idx] || 0),
              scaffoldType1: Number(row[scaffoldType1Idx] || 0),
              loadingLevelCompact: Number(row[loadingLevelCompactIdx] || 0),
              loadingGeotextileLayer: Number(
                row[loadingGeotextileLayerIdx] || 0
              ),
              loadingCrush6F2: Number(row[loadingCrush6F2Idx] || 0),
              loadingType1: Number(row[loadingType1Idx] || 0),
              stairLevelCompact: Number(row[stairLevelCompactIdx] || 0),
              stairGeotextileLayer: Number(row[stairGeotextileLayerIdx] || 0),
              stairCrush6F2: Number(row[stairCrush6F2Idx] || 0),
              stairType1: Number(row[stairType1Idx] || 0),
            });
          });


          if (allImportedRows.length === 0) {
            alert("No valid data found to import");
            return;
          }

          setData(allImportedRows);
          updateField("groundworks", {
            ...tender.groundworks,
            scaffoldTable: allImportedRows,
          });
          alert(`Successfully imported ${allImportedRows.length} rows`);
        } catch (error) {
          console.error("Import error:", error);
          alert(`Import failed: ${error.message}`);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Import error:", error);
      alert(`Import failed: ${error.message}`);
    }
  };

  useEffect(() => {
    if (tender?.groundworks?.scaffoldTable) {
      const tableData = tender.groundworks.scaffoldTable as any[];
      const hasPlotType = tableData.some((row: any) => row.plotType === "plot" || row.plotType === "garage");
      const hasSeparator = tableData.some((row: any) => {
        const pn = String(row.plotNo || "").trim().toUpperCase();
        return pn === "GARAGES" || pn === "GARAGE";
      });

      if (hasPlotType && !hasSeparator) {
        setData(tableData);
      } else {
        // Either old data without plotType, or has a GARAGES separator to remove
        let isGarageSection = false;
        const processedData: ScaffoldRow[] = [];
        tableData.forEach((row: any) => {
          const plotNoUpper = String(row.plotNo || "").trim().toUpperCase();
          if (plotNoUpper === "GARAGES" || plotNoUpper === "GARAGE") {
            isGarageSection = true;
            return; // Skip the separator row
          }
          processedData.push({
            ...row,
            plotType: row.plotType || (isGarageSection ? "garage" : "plot"),
          });
        });
        setData(processedData);
        updateField("groundworks", {
          ...tender.groundworks,
          scaffoldTable: processedData,
        });
      }
    } else {
      // Initialize with empty row
      setData([createEmptyRow()]);
    }
  }, [tender?.groundworks?.scaffoldTable]);

  // Load dimension values and column headers from tender context on mount
  useEffect(() => {
    if (tender?.groundworks?.scaffoldDimensionValues) {
      setDimensionValues(tender.groundworks.scaffoldDimensionValues);
    }
    if (tender?.groundworks?.scaffoldColumnHeaders) {
      setColumnHeaders(tender.groundworks.scaffoldColumnHeaders);
    }
    // Mark initial load as complete after a short delay
    const timer = setTimeout(() => {
      setIsInitialLoadComplete(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Sync dimension values and column headers to tender context whenever they change (no auto-save; save is triggered explicitly in handleConfigApply)
  useEffect(() => {
    if (isInitialLoadComplete) {
      const currentGroundworks = groundworksRef.current || {};
      updateField("groundworks", {
        ...currentGroundworks,
        scaffoldColumnHeaders: columnHeaders,
        scaffoldDimensionValues: dimensionValues,
      });
    }
  }, [columnHeaders, dimensionValues, isInitialLoadComplete]);

  // Apply configure-headers modal: validate, save temp values directly to context + API
  const handleConfigApply = (): boolean => {
    const lbW = tempDimensionValues["loadingBaySizeW"];
    const lbH = tempDimensionValues["loadingBaySizeH"];
    const lbWValid = lbW && parseFloat(lbW) > 0;
    const lbHValid = lbH && parseFloat(lbH) > 0;
    if ((lbWValid && !lbHValid) || (!lbWValid && lbHValid)) {
      message.error("Loading bay size: both dimensions must be filled or both empty");
      return false;
    }
    const saW = tempDimensionValues["stairAccessSizeW"];
    const saH = tempDimensionValues["stairAccessSizeH"];
    const saWValid = saW && parseFloat(saW) > 0;
    const saHValid = saH && parseFloat(saH) > 0;
    if ((saWValid && !saHValid) || (!saWValid && saHValid)) {
      message.error("Stair Access size: both dimensions must be filled or both empty");
      return false;
    }

    const valuesToApply = { ...tempDimensionValues };
    delete valuesToApply["loadingBaySizeW"];
    delete valuesToApply["loadingBaySizeH"];
    delete valuesToApply["stairAccessSizeW"];
    delete valuesToApply["stairAccessSizeH"];

    setDimensionValues(valuesToApply);
    setColumnHeaders(tempColumnHeaders);

    const currentGroundworks = groundworksRef.current || {};
    updateField("groundworks", {
      ...currentGroundworks,
      scaffoldColumnHeaders: tempColumnHeaders,
      scaffoldDimensionValues: valuesToApply,
    });
    setTimeout(() => saveTender(), 100);
    return true;
  };

  const createEmptyRow = (key?: string, plotType: "plot" | "garage" = "plot"): ScaffoldRow => ({
    key: key || Date.now().toString(),
    plotNo: "",
    housetype: "",
    plotType,
    // Base measurements
    scaffoldPlatform: 0,
    loadingBay4x2m: 0,
    stairAccess3x3m: 0,
    // Scaffold Platform
    scaffoldLevelCompact: 0,
    scaffoldGeotextileLayer: 0,
    scaffoldCrush6F2: 0,
    scaffoldType1: 0,
    // Loading Bays
    loadingLevelCompact: 0,
    loadingGeotextileLayer: 0,
    loadingCrush6F2: 0,
    loadingType1: 0,
    // Stair Access
    stairLevelCompact: 0,
    stairGeotextileLayer: 0,
    stairCrush6F2: 0,
    stairType1: 0,
  });

  const handleInputChange = (
    key: string,
    field: keyof ScaffoldRow,
    value: string | number
  ) => {
    setData((prevData) => {
      const currentRow = prevData.find((row) => row.key === key);
      const oldValue = currentRow ? currentRow[field] : undefined;

      // Track the field change
      // trackFieldChange(field as string, oldValue, value, key);

      const newData = prevData.map((row) => {
        if (row.key === key) {
          return { ...row, [field]: value };
        }
        return row;
      });

      // Update tender context
      updateField("groundworks", {
        ...tender.groundworks,
        scaffoldTable: newData,
      });
      return newData;
    });
  };

  const addRow = () => {
    const newRow = createEmptyRow(undefined, plotTypeFilter === "garages" ? "garage" : "plot");
    // trackRowAdded(newRow.key);
    const newData = [...data, newRow];
    setData(newData);
    updateField("groundworks", {
      ...tender.groundworks,
      scaffoldTable: newData,
    });
  };

  const deleteRow = (key: string) => {
    setKeyToDelete(key);
    setKeysToDelete([]);
    setDeleteModalVisible(true);
  };

  const deleteSelectedRows = () => {
    setKeysToDelete(selectedScaffoldKeys as string[]);
    setKeyToDelete(null);
    setDeleteModalVisible(true);
  };

  const confirmDelete = () => {
    const keysBeingDeleted = keysToDelete.length > 0 ? keysToDelete : keyToDelete ? [keyToDelete] : [];
    if (keysBeingDeleted.length > 0) {
      // trackRowDeleted(keyToDelete);
      const keySet = new Set(keysBeingDeleted);
      const newData = data.filter((row) => !keySet.has(row.key));
      setData(newData);
      // Deleting the last remaining row also clears the ad-hoc extra items
      // grafted onto Scaffold Bill Summary — see CutFills.tsx's identical
      // fix for the reasoning.
      updateField("groundworks", {
        ...tender.groundworks,
        scaffoldTable: newData,
        ...(newData.length === 0 && { scaffoldExtraItems: {} }),
      });
      setSelectedScaffoldKeys([]);
    }
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  // Helper function to get column title - uses dynamic header if available, otherwise falls back to default
  const getColumnTitle = React.useCallback(
    (fieldKey: string, defaultTitle: string): string => {
      // LABEL-ONLY normalisation: convert any "<decimal>mm" to integer mm in
      // the final title. Rate calculations continue to use the raw value.
      // Return stored column header (from Excel import) exactly as-is.
      if (columnHeaders[fieldKey]) {
        return columnHeaders[fieldKey];
      }

      // Fallback: substitute dimension value into the default title pattern and
      // normalise any decimal-mm result (e.g. "0.150mm" → "150mm").
      if (dimensionValues[fieldKey]) {
        let title = defaultTitle;
        title = title.replace(/\b(x{2,}(?:mm)?)\b/gi, () => {
          let value = dimensionValues[fieldKey];
          if (value && !value.toLowerCase().endsWith("mm")) {
            value = value + "mm";
          }
          return value;
        });
        return normaliseDecimalMmInLabel(title);
      }

      return defaultTitle;
    },
    [columnHeaders, dimensionValues]
  );

  // Extract dimension fields from excel columns and categorize by tab
  const dimensionFields = React.useMemo(() => {
    const fieldsWithDimensions: Array<{
      key: string;
      title: string;
      category: "scaffold" | "loading" | "stair";
    }> = [];

    // Check excelColumns for fields with dimension patterns (xxmm, xxxmm, etc.)
    excelColumns.forEach((col) => {
      const defaultTitle = col.title;
      // Check if DEFAULT header contains dimension patterns like xxmm, xxxmm, xxxx
      if (/\b(x{2,}(?:mm)?)\b/gi.test(String(defaultTitle))) {
        // Always use default title for dimension fields card (not imported headers)
        // This ensures the label always shows the pattern (xxmm, xxxmm) for user input

        // Determine category based on field key prefix
        const category = col.key.startsWith("loading")
          ? "loading"
          : col.key.startsWith("stair")
          ? "stair"
          : col.key.startsWith("scaffold")
          ? "scaffold"
          : null;

        if (category) {
          fieldsWithDimensions.push({
            key: col.key,
            title: String(defaultTitle), // Always use default title
            category,
          });
        }
      }
    });

    return fieldsWithDimensions;
  }, []); // Remove columnHeaders dependency since we always use default titles

  // Function to create dimension fields card for specific category

  // Filter data based on plotType dropdown
  const filteredData = data.filter((row) =>
    plotTypeFilter === "plots" ? row.plotType === "plot" || !row.plotType : row.plotType === "garage"
  );

  const calculateTotals = () => {
    const totals: Partial<ScaffoldRow> = {
      scaffoldPlatform: 0,
      loadingBay4x2m: 0,
      stairAccess3x3m: 0,
      scaffoldLevelCompact: 0,
      scaffoldGeotextileLayer: 0,
      scaffoldCrush6F2: 0,
      scaffoldType1: 0,
      loadingLevelCompact: 0,
      loadingGeotextileLayer: 0,
      loadingCrush6F2: 0,
      loadingType1: 0,
      stairLevelCompact: 0,
      stairGeotextileLayer: 0,
      stairCrush6F2: 0,
      stairType1: 0,
    };

    filteredData.forEach((row) => {
      Object.keys(totals).forEach((key) => {
        totals[key] += Math.round((Number(row[key]) || 0) * 1000) / 1000;
      });
    });

    return totals;
  };

  const getScrollConfig = (columns: { width?: number | string }[]) => {
    const totalWidth = columns.reduce((sum, col) => sum + (Number(col.width) || 120), 0);
    const estimatedRowHeight = 50;
    const maxComfortableRows = 12;
    const needsVerticalScroll = filteredData.length > maxComfortableRows;
    return { x: totalWidth, y: needsVerticalScroll ? maxComfortableRows * estimatedRowHeight : undefined };
  };

  const handleSave = () => {
    saveTender();
    // Note: Don't reset changes here - changes persist until "Send for Review"
  };

  const handleSaveAndContinue = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal,
        },
      });

      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      await forceSaveBothBoqEndpoints(tender, "Save and continue from Scaffold", {
        grandTotal: groundworksTotals.grandTotal,
        grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
        preliminariesAmount: groundworksTotals.preliminariesAmount,
        ohpAmount: groundworksTotals.ohpAmount,
        discountAmount: groundworksTotals.discountAmount,
        tenderTotal: groundworksTotals.tenderTotal,
        preliminariesPercent: groundworksTotals.preliminariesPercent,
        ohpPercent: groundworksTotals.ohpPercent,
        discountPercent: groundworksTotals.discountPercent,
      }, user?.name, itemLibraryData);

      message.destroy("saveTender");
      setTimeout(() => navigate("/groundworks/garden"), 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  const handleSaveAndExit = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal,
        },
      });

      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      await forceSaveBothBoqEndpoints(tender, "Save and exit from Scaffold", {
        grandTotal: groundworksTotals.grandTotal,
        grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
        preliminariesAmount: groundworksTotals.preliminariesAmount,
        ohpAmount: groundworksTotals.ohpAmount,
        discountAmount: groundworksTotals.discountAmount,
        tenderTotal: groundworksTotals.tenderTotal,
        preliminariesPercent: groundworksTotals.preliminariesPercent,
        ohpPercent: groundworksTotals.ohpPercent,
        discountPercent: groundworksTotals.discountPercent,
      }, user?.name, itemLibraryData);

      message.destroy("saveTender");
      setTimeout(() => navigate("/"), 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  const baseColumns = [
    {
      title: "Plot No",
      dataIndex: "plotNo",
      key: "plotNo",
      width: 130,
      fixed: "left" as const,
      render: (text: string, record: ScaffoldRow) => {
        const prefix = record.plotType === "garage" ? "G-" : "P-";
        const suffix = text?.startsWith(prefix) ? text.slice(prefix.length) : (text ?? "");
        return (
          <BufferedTextInput
            addonBefore={prefix}
            value={suffix}
            onCommit={(newSuffix) => {
              const newPlotNo = prefix + newSuffix;
              if (isDuplicatePlotNo(data, record.key, newPlotNo)) {
                message.error(`Plot No "${newPlotNo}" is already in use.`);
                return;
              }
              handleInputChange(record.key, "plotNo", newPlotNo);
            }}
            placeholder="1"
            style={{ minWidth: 80 }}
          />
        );
      },
    },
    {
      title: "House type",
      dataIndex: "housetype",
      key: "housetype",
      width: 110,
      fixed: "left" as const,
      render: (text: string, record: ScaffoldRow) => (
        <Input
          value={text}
          onChange={(e) =>
            handleInputChange(record.key, "housetype", e.target.value)
          }
          placeholder="House type"
        />
      ),
    },
  ];

  // Scaffold Platform tab columns
  const scaffoldColumns = [
    ...baseColumns,
    {
      title: "Scaffold Platform",
      dataIndex: "scaffoldPlatform",
      key: "scaffoldPlatform",
      width: 120,
      render: (value: number, record: ScaffoldRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "scaffoldPlatform", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("Level & Compact (m²)"),
      dataIndex: "scaffoldLevelCompact",
      key: "scaffoldLevelCompact",
      width: 110,
      render: (value: number, record: ScaffoldRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "scaffoldLevelCompact", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("Geotextile Layer (m²)"),
      dataIndex: "scaffoldGeotextileLayer",
      key: "scaffoldGeotextileLayer",
      width: 120,
      render: (value: number, record: ScaffoldRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(
              record.key,
              "scaffoldGeotextileLayer",
              newValue
            )
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle(`${getColumnTitle("scaffoldCrush6F2", "xxxmm crush 6F2")} (m²)`),
      dataIndex: "scaffoldCrush6F2",
      key: "scaffoldCrush6F2",
      width: 110,
      render: (value: number, record: ScaffoldRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "scaffoldCrush6F2", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle(`${getColumnTitle("scaffoldType1", "xxmm Type1")} (m²)`),
      dataIndex: "scaffoldType1",
      key: "scaffoldType1",
      width: 110,
      render: (value: number, record: ScaffoldRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "scaffoldType1", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      fixed: "right" as const,
      align: "center" as const,
      render: (_, record: ScaffoldRow) => (
        <Button
          type="text"
          danger
          icon={
            <DeleteOutlined style={{ color: "#ff4d4f", fontSize: "16px" }} />
          }
          onClick={() => deleteRow(record.key)}
          size="small"
        />
      ),
    },
  ];

  // Loading Bays tab columns
  const loadingBaysColumns = [
    ...baseColumns,
    {
      title: (
        <div className="text-center">
          <div>Loading bay{dimensionValues["loadingBaySize"] ? ` ${dimensionValues["loadingBaySize"]}` : ""}</div>
          <div className="text-xs text-gray-500">m²</div>
        </div>
      ),
      dataIndex: "loadingBay4x2m",
      key: "loadingBay4x2m",
      width: 120,
      render: (value: number, record: ScaffoldRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "loadingBay4x2m", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("Level & Compact (m²)"),
      dataIndex: "loadingLevelCompact",
      key: "loadingLevelCompact",
      width: 110,
      render: (value: number, record: ScaffoldRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "loadingLevelCompact", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("Geotextile Layer (m²)"),
      dataIndex: "loadingGeotextileLayer",
      key: "loadingGeotextileLayer",
      width: 120,
      render: (value: number, record: ScaffoldRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(
              record.key,
              "loadingGeotextileLayer",
              newValue
            )
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle(`${getColumnTitle("loadingCrush6F2", "xxxmm crush 6F2")} (m²)`),
      dataIndex: "loadingCrush6F2",
      key: "loadingCrush6F2",
      width: 120,
      render: (value: number, record: ScaffoldRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "loadingCrush6F2", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle(`${getColumnTitle("loadingType1", "xxmm Type1")} (m²)`),
      dataIndex: "loadingType1",
      key: "loadingType1",
      width: 110,
      render: (value: number, record: ScaffoldRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "loadingType1", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      fixed: "right" as const,
      align: "center" as const,
      render: (_, record: ScaffoldRow) => (
        <Button
          type="text"
          danger
          icon={
            <DeleteOutlined style={{ color: "#ff4d4f", fontSize: "16px" }} />
          }
          onClick={() => deleteRow(record.key)}
          size="small"
        />
      ),
    },
  ];

  // Stair Access tab columns
  const stairAccessColumns = [
    ...baseColumns,
    {
      title: (
        <div className="text-center">
          <div>Stair Access{dimensionValues["stairAccessSize"] ? ` ${dimensionValues["stairAccessSize"]}` : ""}</div>
          <div className="text-xs text-gray-500">m²</div>
        </div>
      ),
      dataIndex: "stairAccess3x3m",
      key: "stairAccess3x3m",
      width: 120,
      render: (value: number, record: ScaffoldRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "stairAccess3x3m", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("Level & Compact (m²)"),
      dataIndex: "stairLevelCompact",
      key: "stairLevelCompact",
      width: 110,
      render: (value: number, record: ScaffoldRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "stairLevelCompact", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("Geotextile Layer (m²)"),
      dataIndex: "stairGeotextileLayer",
      key: "stairGeotextileLayer",
      width: 120,
      render: (value: number, record: ScaffoldRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "stairGeotextileLayer", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle(`${getColumnTitle("stairCrush6F2", "xxxmm crush 6F2")} (m²)`),
      dataIndex: "stairCrush6F2",
      key: "stairCrush6F2",
      width: 120,
      render: (value: number, record: ScaffoldRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "stairCrush6F2", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle(`${getColumnTitle("stairType1", "xxmm Type1")} (m²)`),
      dataIndex: "stairType1",
      key: "stairType1",
      width: 110,
      render: (value: number, record: ScaffoldRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "stairType1", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      fixed: "right" as const,
      align: "center" as const,
      render: (_, record: ScaffoldRow) => (
        <Button
          type="text"
          danger
          icon={
            <DeleteOutlined style={{ color: "#ff4d4f", fontSize: "16px" }} />
          }
          onClick={() => deleteRow(record.key)}
          size="small"
        />
      ),
    },
  ];

  return (
    <MainLayout showFooter={false}>
      <Card
        className="shadow-sm scaffold-card"
        bodyStyle={{ padding: 0 }}
        style={{ borderRadius: "8px", overflow: "hidden" }}
      >
        {/* Sticky Header with Title and Tabs */}
        <div
          className="scaffold-sticky-header"
          style={{
            position: "sticky",
            top: "144px",
            backgroundColor: "#fff",
            zIndex: 98,
            borderRadius: "8px 8px 0 0",
            isolation: "isolate",
          }}
        >
          {/* Header Section */}
          <div
            className="px-6 pt-6 pb-4"
            style={{ borderBottom: "1px solid #e5e7eb" }}
          >
            <div className="flex justify-between items-center">
              <div>
                <Title
                  level={3}
                  style={{
                    fontWeight: 600,
                    fontSize: "20px",
                    color: "#111827",
                    marginBottom: "8px",
                  }}
                >
                  Scaffold Platforms
                </Title>
                <Text
                  type="secondary"
                  style={{ fontSize: "14px", color: "#6B7280" }}
                >
                  Configure scaffold platform, loading bays, and stair access
                  specifications for each plot.
                </Text>
              </div>
              <Button
                icon={<SettingOutlined />}
                onClick={() => setConfigModalVisible(true)}
                type="default"
                className="configure-headers-btn"
                style={{
                  height: "32px",
                  borderRadius: "6px",
                  fontWeight: 500,
                  borderColor: "#1890ff",
                  color: "#1890ff",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  flexShrink: 0,
                  marginTop: "18px",
                }}
              >
                Configure Headers
              </Button>
            </div>
          </div>

          {/* Plot Type Filter Dropdown */}
          <div className="px-6" style={{ paddingTop: "16px", paddingBottom: "0px" }}>
            <div className="flex items-center gap-2">
              <Text style={{ fontWeight: 500 }}>View:</Text>
              <Select
                value={plotTypeFilter}
                onChange={setPlotTypeFilter}
                style={{ width: 220 }}
                size="middle"
                className="supplier-select"
              >
                <Option value="plots">Plots</Option>
                <Option value="garages">Garages</Option>
              </Select>
            </div>
          </div>

          {/* Tab Buttons */}
          <div
            className="scaffold-sticky-tabs px-6"
            style={{ paddingTop: "24px", paddingBottom: "0px" }}
          >
            <div
              style={{
                marginBottom: "0px",
                display: "flex",
                gap: "4px",
                flexWrap: "wrap",
              }}
            >
              <Button
                type={activeTab === "scaffold-platform" ? "primary" : "default"}
                onClick={() => setActiveTab("scaffold-platform")}
                className={
                  activeTab === "scaffold-platform"
                    ? "active-tab-button"
                    : "inactive-tab-button"
                }
                style={{
                  backgroundColor:
                    activeTab === "scaffold-platform"
                      ? COLORS.primary
                      : "#E1E6EF",
                  borderColor:
                    activeTab === "scaffold-platform"
                      ? COLORS.primary
                      : "#E1E6EF",
                  color: activeTab === "scaffold-platform" ? "#fff" : "#6B7280",
                  fontWeight: 500,
                  borderRadius: "6px 6px 0 0",
                  height: "38px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  borderBottom:
                    activeTab === "scaffold-platform"
                      ? "none"
                      : "1px solid #E1E6EF",
                  position: "relative",
                  zIndex: activeTab === "scaffold-platform" ? 2 : 1,
                }}
              >
                Scaffold Platforms
              </Button>
              <Button
                type={activeTab === "loading-bays" ? "primary" : "default"}
                onClick={() => setActiveTab("loading-bays")}
                className={
                  activeTab === "loading-bays"
                    ? "active-tab-button"
                    : "inactive-tab-button"
                }
                style={{
                  backgroundColor:
                    activeTab === "loading-bays" ? COLORS.primary : "#E1E6EF",
                  borderColor:
                    activeTab === "loading-bays" ? COLORS.primary : "#E1E6EF",
                  color: activeTab === "loading-bays" ? "#fff" : "#6B7280",
                  fontWeight: 500,
                  borderRadius: "6px 6px 0 0",
                  height: "38px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  borderBottom:
                    activeTab === "loading-bays" ? "none" : "1px solid #E1E6EF",
                  position: "relative",
                  zIndex: activeTab === "loading-bays" ? 2 : 1,
                }}
              >
                Loading Bays
              </Button>
              <Button
                type={activeTab === "stair-access" ? "primary" : "default"}
                onClick={() => setActiveTab("stair-access")}
                className={
                  activeTab === "stair-access"
                    ? "active-tab-button"
                    : "inactive-tab-button"
                }
                style={{
                  backgroundColor:
                    activeTab === "stair-access" ? COLORS.primary : "#E1E6EF",
                  borderColor:
                    activeTab === "stair-access" ? COLORS.primary : "#E1E6EF",
                  color: activeTab === "stair-access" ? "#fff" : "#6B7280",
                  fontWeight: 500,
                  borderRadius: "6px 6px 0 0",
                  height: "38px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  borderBottom:
                    activeTab === "stair-access" ? "none" : "1px solid #E1E6EF",
                  position: "relative",
                  zIndex: activeTab === "stair-access" ? 2 : 1,
                }}
              >
                Stair Access
              </Button>
              {selectedScaffoldKeys.length > 0 && (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={deleteSelectedRows}
                  style={{
                    height: "32px",
                    marginLeft: "auto",
                    borderRadius: "6px",
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Delete Selected ({selectedScaffoldKeys.length})
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Content Section - Tables */}
        <div
          className="px-6 pb-6 scaffold-content-section"
          style={{
            paddingTop: "0px",
            position: "relative",
            zIndex: 1,
            marginTop: "0px",
          }}
        >
          {/* Scaffold Platform Table */}
          {activeTab === "scaffold-platform" && (
            <div>
              <div
                className="scaffold-sticky-dimension-scaffold"
                style={{
                  position: "sticky",
                  top:
                    144 +
                    document
                      .getElementsByClassName("scaffold-sticky-header")?.[0]
                      ?.getBoundingClientRect?.()?.height +
                    "px",
                  backgroundColor: "#fff",
                  zIndex: 98,
                }}
              ></div>

              {isTableLoading ? (
                <TableLoadingPlaceholder />
              ) : (
              <Table
                key={tableKey}
                dataSource={filteredData}
                columns={addColumnCodes(scaffoldColumns, SCAFFOLD_REFERENCES)}
                pagination={false}
                rowSelection={{
                  selectedRowKeys: selectedScaffoldKeys,
                  onChange: setSelectedScaffoldKeys,
                  fixed: "left",
                  columnWidth: 48,
                }}
                scroll={getScrollConfig(scaffoldColumns)}
                sticky={{
                  offsetHeader:
                    144 +
                    document
                      .getElementsByClassName("scaffold-sticky-header")?.[0]
                      ?.getBoundingClientRect?.()?.height +
                    document
                      .getElementsByClassName(
                        "scaffold-sticky-dimension-scaffold"
                      )?.[0]
                      ?.getBoundingClientRect?.()?.height,
                }}
                size="middle"
                bordered
                className="scaffold-table"
                summary={() => {
                  if (filteredData.length === 0) return null;
                  const totals = calculateTotals();
                  return (
                    <Table.Summary fixed="bottom">
                      <Table.Summary.Row
                        style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                      >
                        <Table.Summary.Cell index={0} colSpan={3} align="center">
                          <span style={{ color: "#000" }}>Totals</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right">
                          <span
                            style={{
                              color: "#000",
                              textAlign: "right",
                              display: "block",
                              paddingRight: "8px",
                            }}
                          >
                            {totals.scaffoldPlatform?.toFixed(3)}
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="right">
                          <span
                            style={{
                              color: "#000",
                              textAlign: "right",
                              display: "block",
                              paddingRight: "8px",
                            }}
                          >
                            {totals.scaffoldLevelCompact?.toFixed(3)}
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="right">
                          <span
                            style={{
                              color: "#000",
                              textAlign: "right",
                              display: "block",
                              paddingRight: "8px",
                            }}
                          >
                            {totals.scaffoldGeotextileLayer?.toFixed(3)}
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6} align="right">
                          <span
                            style={{
                              color: "#000",
                              textAlign: "right",
                              display: "block",
                              paddingRight: "8px",
                            }}
                          >
                            {totals.scaffoldCrush6F2?.toFixed(3)}
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={7} align="right">
                          <span
                            style={{
                              color: "#000",
                              textAlign: "right",
                              display: "block",
                              paddingRight: "8px",
                            }}
                          >
                            {totals.scaffoldType1?.toFixed(3)}
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={8}></Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
              )}
              <div style={{ marginTop: "16px" }}>
                <Button
                  type="text"
                  icon={<PlusOutlined style={{ color: COLORS.primary }} />}
                  onClick={addRow}
                  style={{
                    color: COLORS.primary,
                    fontWeight: 500,
                    padding: "4px 8px",
                  }}
                >
                  Add New Row
                </Button>
              </div>
            </div>
          )}

          {/* Loading Bays Table */}
          {activeTab === "loading-bays" && (
            <div>
              <div
                className="scaffold-sticky-dimension-loading"
                style={{
                  position: "sticky",
                  top:
                    144 +
                    document
                      .getElementsByClassName("scaffold-sticky-header")?.[0]
                      ?.getBoundingClientRect?.()?.height +
                    "px",
                  backgroundColor: "#fff",
                  zIndex: 98,
                }}
              ></div>

              {isTableLoading ? (
                <TableLoadingPlaceholder />
              ) : (
              <Table
                key={tableKey}
                dataSource={filteredData}
                columns={addColumnCodes(loadingBaysColumns, SCAFFOLD_REFERENCES)}
                pagination={false}
                rowSelection={{
                  selectedRowKeys: selectedScaffoldKeys,
                  onChange: setSelectedScaffoldKeys,
                  fixed: "left",
                  columnWidth: 48,
                }}
                scroll={getScrollConfig(loadingBaysColumns)}
                sticky={{
                  offsetHeader:
                    144 +
                    document
                      .getElementsByClassName("scaffold-sticky-header")?.[0]
                      ?.getBoundingClientRect?.()?.height +
                    document
                      .getElementsByClassName(
                        "scaffold-sticky-dimension-loading"
                      )?.[0]
                      ?.getBoundingClientRect?.()?.height,
                }}
                size="middle"
                bordered
                className="scaffold-table"
                summary={() => {
                  if (filteredData.length === 0) return null;
                  const totals = calculateTotals();
                  return (
                    <Table.Summary fixed="bottom">
                      <Table.Summary.Row
                        style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                      >
                        <Table.Summary.Cell index={0} colSpan={3} align="center">
                          <span style={{ color: "#000" }}>Totals</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right">
                          <span
                            style={{
                              color: "#000",
                              textAlign: "right",
                              display: "block",
                              paddingRight: "8px",
                            }}
                          >
                            {totals.loadingBay4x2m?.toFixed(3)}
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="right">
                          <span
                            style={{
                              color: "#000",
                              textAlign: "right",
                              display: "block",
                              paddingRight: "8px",
                            }}
                          >
                            {totals.loadingLevelCompact?.toFixed(3)}
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="right">
                          <span
                            style={{
                              color: "#000",
                              textAlign: "right",
                              display: "block",
                              paddingRight: "8px",
                            }}
                          >
                            {totals.loadingGeotextileLayer?.toFixed(3)}
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6} align="right">
                          <span
                            style={{
                              color: "#000",
                              textAlign: "right",
                              display: "block",
                              paddingRight: "8px",
                            }}
                          >
                            {totals.loadingCrush6F2?.toFixed(3)}
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={7} align="right">
                          <span
                            style={{
                              color: "#000",
                              textAlign: "right",
                              display: "block",
                              paddingRight: "8px",
                            }}
                          >
                            {totals.loadingType1?.toFixed(3)}
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={8}></Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
              )}
              <div style={{ marginTop: "16px" }}>
                <Button
                  type="text"
                  icon={<PlusOutlined style={{ color: COLORS.primary }} />}
                  onClick={addRow}
                  style={{
                    color: COLORS.primary,
                    fontWeight: 500,
                    padding: "4px 8px",
                  }}
                >
                  Add New Row
                </Button>
              </div>
            </div>
          )}

          {/* Stair Access Table */}
          {activeTab === "stair-access" && (
            <div>
              <div
                className="scaffold-sticky-dimension-stair"
                style={{
                  position: "sticky",
                  top:
                    144 +
                    document
                      .getElementsByClassName("scaffold-sticky-header")?.[0]
                      ?.getBoundingClientRect?.()?.height +
                    "px",
                  backgroundColor: "#fff",
                  zIndex: 98,
                }}
              ></div>

              {isTableLoading ? (
                <TableLoadingPlaceholder />
              ) : (
              <Table
                key={tableKey}
                dataSource={filteredData}
                columns={addColumnCodes(stairAccessColumns, SCAFFOLD_REFERENCES)}
                pagination={false}
                rowSelection={{
                  selectedRowKeys: selectedScaffoldKeys,
                  onChange: setSelectedScaffoldKeys,
                  fixed: "left",
                  columnWidth: 48,
                }}
                scroll={getScrollConfig(stairAccessColumns)}
                sticky={{
                  offsetHeader:
                    144 +
                    document
                      .getElementsByClassName("scaffold-sticky-header")?.[0]
                      ?.getBoundingClientRect?.()?.height +
                    document
                      .getElementsByClassName(
                        "scaffold-sticky-dimension-stair"
                      )?.[0]
                      ?.getBoundingClientRect?.()?.height,
                }}
                size="middle"
                bordered
                className="scaffold-table"
                summary={() => {
                  if (filteredData.length === 0) return null;
                  const totals = calculateTotals();
                  return (
                    <Table.Summary fixed="bottom">
                      <Table.Summary.Row
                        style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                      >
                        <Table.Summary.Cell index={0} colSpan={3} align="center">
                          <span style={{ color: "#000" }}>Totals</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right">
                          <span
                            style={{
                              color: "#000",
                              textAlign: "right",
                              display: "block",
                              paddingRight: "8px",
                            }}
                          >
                            {totals.stairAccess3x3m?.toFixed(3)}
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="right">
                          <span
                            style={{
                              color: "#000",
                              textAlign: "right",
                              display: "block",
                              paddingRight: "8px",
                            }}
                          >
                            {totals.stairLevelCompact?.toFixed(3)}
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="right">
                          <span
                            style={{
                              color: "#000",
                              textAlign: "right",
                              display: "block",
                              paddingRight: "8px",
                            }}
                          >
                            {totals.stairGeotextileLayer?.toFixed(3)}
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6} align="right">
                          <span
                            style={{
                              color: "#000",
                              textAlign: "right",
                              display: "block",
                              paddingRight: "8px",
                            }}
                          >
                            {totals.stairCrush6F2?.toFixed(3)}
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={7} align="right">
                          <span
                            style={{
                              color: "#000",
                              textAlign: "right",
                              display: "block",
                              paddingRight: "8px",
                            }}
                          >
                            {totals.stairType1?.toFixed(3)}
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={8}></Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
              )}
              <div style={{ marginTop: "16px" }}>
                <Button
                  type="text"
                  icon={<PlusOutlined style={{ color: COLORS.primary }} />}
                  onClick={addRow}
                  style={{
                    color: COLORS.primary,
                    fontWeight: 500,
                    padding: "4px 8px",
                  }}
                >
                  Add New Row
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer with action buttons */}
        <div style={{ borderTop: "1px solid #e5e7eb" }}>
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              onClick={handleSaveAndExit}
              style={{
                borderColor: COLORS.primary,
                color: COLORS.primary,
              }}
            >
              Save and Exit
            </Button>
            <Button
              type="primary"
              onClick={handleSaveAndContinue}
              style={{
                backgroundColor: COLORS.primary,
                borderColor: COLORS.primary,
              }}
            >
              Save & Continue <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      </Card>

      {/* Hidden Excel Import/Export Component */}
      <div style={{ display: "none" }}>
        <ExcelImportExport
          onExport={handleExportExcel}
          onImport={handleImportExcel}
          onDownloadTemplate={handleDownloadTemplate}
          importLoading={importLoading}
          importModalVisible={importModalVisible}
          setImportModalVisible={setImportModalVisible}
          hasData={data.length > 0}
        />
      </div>

      <style>{`
        .scaffold-card.ant-card {
          border-radius: 8px !important;
          overflow: visible !important;
        }

        .scaffold-card.ant-card .ant-card-body {
          border-radius: 8px !important;
          overflow: visible !important;
          position: relative !important;
        }

        .scaffold-sticky-header {
          box-shadow: none;
          will-change: transform;
          transform: translateZ(0);
          z-index: 98 !important;
        }

        .scaffold-sticky-tabs {
          border-bottom: none;
          min-height: 40px;
        }

        .scaffold-card {
          position: relative;
          height: auto;
          overflow: visible !important;
        }

        .scaffold-card .ant-card-body {
          overflow: visible !important;
        }

        .scaffold-content-section {
          clip-path: inset(0px -100vw -100vh -100vw);
        }

        .scaffold-table .ant-table-body {
          margin-top: 0 !important;
        }

        .active-tab-button {
          border-bottom: none !important;
        }

        .inactive-tab-button {
          border-bottom: none !important;
        }

        .scaffold-table {
          font-size: 14px;
          margin-top: 0 !important;
        }

        .scaffold-table .ant-table-wrapper {
          margin-top: -1px !important;
        }

        .scaffold-table .ant-table-container {
          margin-top: 0 !important;
        }

        .scaffold-table .ant-table {
          border: none !important;
          margin-top: 0 !important;
        }

        .scaffold-table .ant-table-container {
          border: 1px solid #e5e7eb !important;
          border-top: none !important;
          border-radius: 0 !important;
        }

        .scaffold-table .ant-table-container {
          border: 1px solid #e5e7eb !important;
          border-top: 1px solid #e5e7eb !important;
        }

        .scaffold-table .ant-table-thead > tr > th {
          min-width: 110px !important;
          background-color: ${COLORS.tableHeaderBg} !important;
          font-weight: 600 !important;
          color: #000 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          padding: 24px 8px 10px 8px !important;
          text-align: center !important;
          font-size: 13px !important;
          will-change: transform !important;
          transform: translateZ(0) !important;
          z-index: 97 !important;
        }

        .scaffold-table .ant-table-thead > tr > th.ant-table-cell-fix-left {
          z-index: 99 !important;
        }

        .scaffold-table .ant-table-thead > tr > th.ant-table-cell-fix-right {
          z-index: 100 !important;
          background-color: ${COLORS.tableHeaderBg} !important;
        }

        .scaffold-table .ant-table-tbody > tr > td.ant-table-cell-fix-right {
          z-index: 2 !important;
          background-color: #fff !important;
        }

        .scaffold-table .ant-table-tbody > tr:hover > td.ant-table-cell-fix-right {
          background-color: #fafafa !important;
        }

        .scaffold-table .ant-table-thead > tr > th:first-child {
          border-left: none !important;
        }

        /* The generic min-width:110px above also matches the rowSelection
           checkbox column since it's a plain th/td. Keep it pinned to the
           rowSelection columnWidth instead of being stretched. */
        .scaffold-table .ant-table-thead > tr > th.ant-table-selection-column,
        .scaffold-table .ant-table-tbody > tr > td.ant-table-selection-column {
          min-width: 48px !important;
          width: 48px !important;
        }

        .scaffold-table .ant-table-tbody > tr > td {
          min-width: 110px !important;
          padding: 6px 8px !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          background-color: #fff;
        }

        .scaffold-table .ant-table-tbody > tr > td:first-child {
          border-left: none !important;
        }

        .scaffold-table .ant-table-tbody > tr:hover > td {
          background-color: #fafafa !important;
        }

        /* Bulk-select checkboxes should not highlight the row — keep the normal/hover backgrounds */
        .scaffold-table .ant-table-tbody > tr.ant-table-row-selected > td {
          background-color: #fff !important;
        }

        .scaffold-table .ant-table-tbody > tr.ant-table-row-selected:hover > td {
          background-color: #fafafa !important;
        }

        .scaffold-table .ant-input {
          border: 1px solid #d9d9d9 !important;
          border-radius: 4px !important;
          padding: 4px 11px !important;
          font-size: 14px !important;
          height: 32px !important;
        }

        /* Center-align text inputs by default (for Plot No, Housetype) */
        .scaffold-table .ant-input[type="text"] {
          text-align: center !important;
        }

        /* Right-align NumberInput components (exclude checkbox, Plot No, Housetype columns) */
        .scaffold-table td:not(:nth-child(1)):not(:nth-child(2)):not(:nth-child(3)) .ant-input[type="text"] {
          text-align: right !important;
        }

        .scaffold-table .ant-input:focus {
          border-color: ${COLORS.primary} !important;
          box-shadow: 0 0 0 2px rgba(22, 126, 249, 0.1) !important;
        }

        .scaffold-table .ant-input-number {
          width: 100%;
        }

        .scaffold-table .ant-input[type="number"] {
          text-align: right !important;
        }

        .scaffold-table .ant-table-summary > tr > td {
          background-color: #FFF7E6 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: none !important;
          border-left: none !important;
          border-top: none !important;
          padding: 10px 8px !important;
          font-weight: 600 !important;
        }

        .scaffold-table .ant-table-summary > tr > td:first-child {
          border-left: none !important;
        }

        .scaffold-table .ant-btn-text.ant-btn-dangerous {
          color: #ff4d4f !important;
        }

        .scaffold-table .ant-btn-text.ant-btn-dangerous:hover {
          background-color: #fff1f0 !important;
        }
      `}</style>

      {/* Column Configuration Modal */}
      <ColumnConfigModal
        title="Configure Headers"
        open={configModalVisible}
        onClose={() => {
          // Reset temp values back to actual values when modal closes without applying
          setTempDimensionValues({ ...dimensionValues });
          setTempColumnHeaders({ ...columnHeaders });
          setConfigModalVisible(false);
        }}
        onApply={handleConfigApply}
        width={500}
        isViewMode={tender.isViewMode}
      >
        {activeTab === "scaffold-platform" ? (
          <div className="grid grid-cols-2 gap-3">
            {/* Scaffold Platform - xxxmm crush 6F2 */}
            <div>
              <Text className="text-xs mb-1 block">xxxmm crush 6F2 (m²)</Text>
              <NumberInput
                value={(() => {
                  const raw = parseFloat(tempDimensionValues["scaffoldCrush6F2"]) || 0;
                  return raw >= 1 ? raw / 1000 : raw;
                })()}
                onChange={(value) => {
                  const metresValue = value ? parseFloat(value.toString()) : 0;
                  if (!metresValue) {
                    setTempDimensionValues((prev) => {
                      const newValues = { ...prev };
                      delete newValues["scaffoldCrush6F2"];
                      return newValues;
                    });
                    setTempColumnHeaders((prev) => {
                      const newHeaders = { ...prev };
                      delete newHeaders["scaffoldCrush6F2"];
                      return newHeaders;
                    });
                  } else {
                    const mmValue = String(Math.round(metresValue * 1000));
                    setTempDimensionValues((prev) => ({
                      ...prev,
                      scaffoldCrush6F2: mmValue,
                    }));
                    setTempColumnHeaders((prev) => ({
                      ...prev,
                      scaffoldCrush6F2: `${mmValue}mm crush 6F2 (m²)`,
                    }));
                  }
                }}
                positiveOnly
                decimalPlaces={3}
                zeroIntegerOnly
              />
            </div>
            {/* Scaffold Platform - xxmm Type1 */}
            <div>
              <Text className="text-xs mb-1 block">xxmm Type1 (m²)</Text>
              <NumberInput
                value={(() => {
                  const raw = parseFloat(tempDimensionValues["scaffoldType1"]) || 0;
                  return raw >= 1 ? raw / 1000 : raw;
                })()}
                onChange={(value) => {
                  const metresValue = value ? parseFloat(value.toString()) : 0;
                  if (!metresValue) {
                    setTempDimensionValues((prev) => {
                      const newValues = { ...prev };
                      delete newValues["scaffoldType1"];
                      return newValues;
                    });
                    setTempColumnHeaders((prev) => {
                      const newHeaders = { ...prev };
                      delete newHeaders["scaffoldType1"];
                      return newHeaders;
                    });
                  } else {
                    const mmValue = String(Math.round(metresValue * 1000));
                    setTempDimensionValues((prev) => ({
                      ...prev,
                      scaffoldType1: mmValue,
                    }));
                    setTempColumnHeaders((prev) => ({
                      ...prev,
                      scaffoldType1: `${mmValue}mm Type1 (m²)`,
                    }));
                  }
                }}
                positiveOnly
                decimalPlaces={3}
                zeroIntegerOnly
              />
            </div>
            {/* Scaffold Platform - Geotextile Layer */}
            <div className="col-span-2">
              <Checkbox
                checked={tempDimensionValues["scaffoldGeotextileEnabled"] === "true"}
                onChange={(e) =>
                  setTempDimensionValues((prev) => ({
                    ...prev,
                    scaffoldGeotextileEnabled: e.target.checked ? "true" : "false",
                  }))
                }
              >
                Geotextile Layer
              </Checkbox>
            </div>
          </div>
        ) : activeTab === "loading-bays" ? (
          <div className="grid grid-cols-1 gap-3">
            {/* Loading Bay Size */}
            <div>
              <Text className="text-xs mb-1 block">Loading bay size</Text>
              <div className="flex items-center gap-1">
                <InputNumber
                  value={
                    tempDimensionValues["loadingBaySizeW"]
                      ? parseFloat(tempDimensionValues["loadingBaySizeW"])
                      : undefined
                  }
                  onChange={(value) => {
                    const strValue = value?.toString() || "";
                    if (!strValue || parseFloat(strValue) <= 0) {
                      setTempDimensionValues((prev) => {
                        const newValues = { ...prev };
                        delete newValues["loadingBaySizeW"];
                        delete newValues["loadingBaySize"];
                        const h = newValues["loadingBaySizeH"];
                        if (!h || parseFloat(h) <= 0) {
                          delete newValues["loadingBaySizeH"];
                        }
                        return newValues;
                      });
                    } else {
                      setTempDimensionValues((prev) => {
                        const newValues = { ...prev, loadingBaySizeW: strValue };
                        const h = newValues["loadingBaySizeH"];
                        if (h && parseFloat(h) > 0) {
                          newValues["loadingBaySize"] = `${strValue}x${h}m`;
                        } else {
                          delete newValues["loadingBaySize"];
                        }
                        return newValues;
                      });
                    }
                  }}
                  min={0}
                  placeholder="0"
                  style={{ width: "100%" }}
                />
                <span className="text-xs font-medium">x</span>
                <InputNumber
                  value={
                    tempDimensionValues["loadingBaySizeH"]
                      ? parseFloat(tempDimensionValues["loadingBaySizeH"])
                      : undefined
                  }
                  onChange={(value) => {
                    const strValue = value?.toString() || "";
                    if (!strValue || parseFloat(strValue) <= 0) {
                      setTempDimensionValues((prev) => {
                        const newValues = { ...prev };
                        delete newValues["loadingBaySizeH"];
                        delete newValues["loadingBaySize"];
                        const w = newValues["loadingBaySizeW"];
                        if (!w || parseFloat(w) <= 0) {
                          delete newValues["loadingBaySizeW"];
                        }
                        return newValues;
                      });
                    } else {
                      setTempDimensionValues((prev) => {
                        const newValues = { ...prev, loadingBaySizeH: strValue };
                        const w = newValues["loadingBaySizeW"];
                        if (w && parseFloat(w) > 0) {
                          newValues["loadingBaySize"] = `${w}x${strValue}m`;
                        } else {
                          delete newValues["loadingBaySize"];
                        }
                        return newValues;
                      });
                    }
                  }}
                  min={0}
                  placeholder="0"
                  style={{ width: "100%" }}
                />
                <span className="text-xs font-medium">m</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
            {/* Loading Bays - xxxmm crush 6F2 */}
            <div>
              <Text className="text-xs mb-1 block">xxxmm crush 6F2 (m²)</Text>
              <NumberInput
                value={(() => {
                  const raw = parseFloat(tempDimensionValues["loadingCrush6F2"]) || 0;
                  return raw >= 1 ? raw / 1000 : raw;
                })()}
                onChange={(value) => {
                  const metresValue = value ? parseFloat(value.toString()) : 0;
                  if (!metresValue) {
                    setTempDimensionValues((prev) => {
                      const newValues = { ...prev };
                      delete newValues["loadingCrush6F2"];
                      return newValues;
                    });
                    setTempColumnHeaders((prev) => {
                      const newHeaders = { ...prev };
                      delete newHeaders["loadingCrush6F2"];
                      return newHeaders;
                    });
                  } else {
                    const mmValue = String(Math.round(metresValue * 1000));
                    setTempDimensionValues((prev) => ({
                      ...prev,
                      loadingCrush6F2: mmValue,
                    }));
                    setTempColumnHeaders((prev) => ({
                      ...prev,
                      loadingCrush6F2: `${mmValue}mm crush 6F2 (m²)`,
                    }));
                  }
                }}
                positiveOnly
                decimalPlaces={3}
                zeroIntegerOnly
              />
            </div>
            {/* Loading Bays - xxmm Type1 */}
            <div>
              <Text className="text-xs mb-1 block">xxmm Type1 (m²)</Text>
              <NumberInput
                value={(() => {
                  const raw = parseFloat(tempDimensionValues["loadingType1"]) || 0;
                  return raw >= 1 ? raw / 1000 : raw;
                })()}
                onChange={(value) => {
                  const metresValue = value ? parseFloat(value.toString()) : 0;
                  if (!metresValue) {
                    setTempDimensionValues((prev) => {
                      const newValues = { ...prev };
                      delete newValues["loadingType1"];
                      return newValues;
                    });
                    setTempColumnHeaders((prev) => {
                      const newHeaders = { ...prev };
                      delete newHeaders["loadingType1"];
                      return newHeaders;
                    });
                  } else {
                    const mmValue = String(Math.round(metresValue * 1000));
                    setTempDimensionValues((prev) => ({
                      ...prev,
                      loadingType1: mmValue,
                    }));
                    setTempColumnHeaders((prev) => ({
                      ...prev,
                      loadingType1: `${mmValue}mm Type1 (m²)`,
                    }));
                  }
                }}
                positiveOnly
                decimalPlaces={3}
                zeroIntegerOnly
              />
            </div>
            </div>
            {/* Loading Bays - Geotextile Layer */}
            <div>
              <Checkbox
                checked={tempDimensionValues["loadingGeotextileEnabled"] === "true"}
                onChange={(e) =>
                  setTempDimensionValues((prev) => ({
                    ...prev,
                    loadingGeotextileEnabled: e.target.checked ? "true" : "false",
                  }))
                }
              >
                Geotextile Layer
              </Checkbox>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {/* Stair Access Size */}
            <div>
              <Text className="text-xs mb-1 block">Stair Access size</Text>
              <div className="flex items-center gap-1">
                <InputNumber
                  value={
                    tempDimensionValues["stairAccessSizeW"]
                      ? parseFloat(tempDimensionValues["stairAccessSizeW"])
                      : undefined
                  }
                  onChange={(value) => {
                    const strValue = value?.toString() || "";
                    if (!strValue || parseFloat(strValue) <= 0) {
                      setTempDimensionValues((prev) => {
                        const newValues = { ...prev };
                        delete newValues["stairAccessSizeW"];
                        delete newValues["stairAccessSize"];
                        const h = newValues["stairAccessSizeH"];
                        if (!h || parseFloat(h) <= 0) {
                          delete newValues["stairAccessSizeH"];
                        }
                        return newValues;
                      });
                    } else {
                      setTempDimensionValues((prev) => {
                        const newValues = { ...prev, stairAccessSizeW: strValue };
                        const h = newValues["stairAccessSizeH"];
                        if (h && parseFloat(h) > 0) {
                          newValues["stairAccessSize"] = `${strValue}x${h}m`;
                        } else {
                          delete newValues["stairAccessSize"];
                        }
                        return newValues;
                      });
                    }
                  }}
                  min={0}
                  placeholder="0"
                  style={{ width: "100%" }}
                />
                <span className="text-xs font-medium">x</span>
                <InputNumber
                  value={
                    tempDimensionValues["stairAccessSizeH"]
                      ? parseFloat(tempDimensionValues["stairAccessSizeH"])
                      : undefined
                  }
                  onChange={(value) => {
                    const strValue = value?.toString() || "";
                    if (!strValue || parseFloat(strValue) <= 0) {
                      setTempDimensionValues((prev) => {
                        const newValues = { ...prev };
                        delete newValues["stairAccessSizeH"];
                        delete newValues["stairAccessSize"];
                        const w = newValues["stairAccessSizeW"];
                        if (!w || parseFloat(w) <= 0) {
                          delete newValues["stairAccessSizeW"];
                        }
                        return newValues;
                      });
                    } else {
                      setTempDimensionValues((prev) => {
                        const newValues = { ...prev, stairAccessSizeH: strValue };
                        const w = newValues["stairAccessSizeW"];
                        if (w && parseFloat(w) > 0) {
                          newValues["stairAccessSize"] = `${w}x${strValue}m`;
                        } else {
                          delete newValues["stairAccessSize"];
                        }
                        return newValues;
                      });
                    }
                  }}
                  min={0}
                  placeholder="0"
                  style={{ width: "100%" }}
                />
                <span className="text-xs font-medium">m</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
            {/* Stair Access - xxxmm crush 6F2 */}
            <div>
              <Text className="text-xs mb-1 block">xxxmm crush 6F2 (m²)</Text>
              <NumberInput
                value={
                  tempDimensionValues["stairCrush6F2"]
                    ? parseFloat(tempDimensionValues["stairCrush6F2"])
                    : 0
                }
                onChange={(value) => {
                  const strValue = value?.toString() || "";
                  if (!strValue || strValue === "0") {
                    setTempDimensionValues((prev) => {
                      const newValues = { ...prev };
                      delete newValues["stairCrush6F2"];
                      return newValues;
                    });
                    setTempColumnHeaders((prev) => {
                      const newHeaders = { ...prev };
                      delete newHeaders["stairCrush6F2"];
                      return newHeaders;
                    });
                  } else {
                    setTempDimensionValues((prev) => ({
                      ...prev,
                      stairCrush6F2: strValue,
                    }));
                    const formattedValue = strValue.includes("mm")
                      ? strValue
                      : `${strValue}mm`;
                    setTempColumnHeaders((prev) => ({
                      ...prev,
                      stairCrush6F2: `${formattedValue} crush 6F2 (m²)`,
                    }));
                  }
                }}
                positiveOnly
                decimalPlaces={3}
                zeroIntegerOnly
              />
            </div>
            {/* Stair Access - xxmm Type1 */}
            <div>
              <Text className="text-xs mb-1 block">xxmm Type1 (m²)</Text>
              <NumberInput
                value={
                  tempDimensionValues["stairType1"]
                    ? parseFloat(tempDimensionValues["stairType1"])
                    : 0
                }
                onChange={(value) => {
                  const strValue = value?.toString() || "";
                  if (!strValue || strValue === "0") {
                    setTempDimensionValues((prev) => {
                      const newValues = { ...prev };
                      delete newValues["stairType1"];
                      return newValues;
                    });
                    setTempColumnHeaders((prev) => {
                      const newHeaders = { ...prev };
                      delete newHeaders["stairType1"];
                      return newHeaders;
                    });
                  } else {
                    setTempDimensionValues((prev) => ({
                      ...prev,
                      stairType1: strValue,
                    }));
                    const formattedValue = strValue.includes("mm")
                      ? strValue
                      : `${strValue}mm`;
                    setTempColumnHeaders((prev) => ({
                      ...prev,
                      stairType1: `${formattedValue} Type1 (m²)`,
                    }));
                  }
                }}
                positiveOnly
                decimalPlaces={3}
                zeroIntegerOnly
              />
            </div>
            </div>
            {/* Stair Access - Geotextile Layer */}
            <div>
              <Checkbox
                checked={tempDimensionValues["stairGeotextileEnabled"] === "true"}
                onChange={(e) =>
                  setTempDimensionValues((prev) => ({
                    ...prev,
                    stairGeotextileEnabled: e.target.checked ? "true" : "false",
                  }))
                }
              >
                Geotextile Layer
              </Checkbox>
            </div>
          </div>
        )}
      </ColumnConfigModal>

      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </MainLayout>
  );
};

export default Scaffold;
